import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Wallet, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";
import { formatINR, formatDate, walletTxTypeLabel } from "../../lib/format";
import { useAuth } from "../../context/AuthContext";
import ReferralTrackingPanel from "../../components/ReferralTrackingPanel";
import MilestoneProgressWidget from "../../components/MilestoneProgressWidget";
import MilestoneHistoryPanel from "../../components/MilestoneHistoryPanel";
import IncentiveCommissionBoxes from "../../components/IncentiveCommissionBoxes";

export default function BrokerEarnings() {
  const { user } = useAuth();
  const [w, setW] = useState(null);
  const [upi, setUpi] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const load = async () => {
    try {
      const { data } = await api.get("/wallet");
      setW(data);
      setError("");
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail, "Failed to load earnings"));
      setW({ balance_paise: 0, total_earned_paise: 0, total_withdrawn_paise: 0, transactions: [] });
    }
  };
  useEffect(() => { load(); }, []);

  const balance = w?.balance_paise || 0;
  const ready = balance >= 50000;
  const progress = Math.min(100, (balance / 50000) * 100);

  const withdraw = async () => {
    if (user.kyc_status !== "verified") { toast.error("Verify your identity first."); return; }
    setBusy(true);
    try {
      await api.post("/wallet/withdraw", { upi_id: upi, amount_paise: balance });
      toast.success(`${formatINR(balance)} sent to ${upi}!`);
      setUpi(""); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (!w) return <div className="p-5 text-gray-500">Loading…</div>;

  return (
    <div className="space-y-6" data-testid="broker-earnings-page">
      <div>
        <h1 className="text-2xl font-bold font-display">My Earnings</h1>
        <p className="text-sm text-gray-500 mt-1">Commissions from deals you close directly.</p>
      </div>

      {error && (
        <div className="rounded-xl bg-red-50 border border-red-200 p-4 text-red-700 text-sm">
          {error}
        </div>
      )}

      <IncentiveCommissionBoxes wallet={w} />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="card-base p-5"><div className="text-xs text-gray-500 uppercase">Total earned</div><div className="mt-2 text-2xl font-bold font-display">{formatINR(w.total_earned_paise)}</div></div>
        <div className="card-base p-5"><div className="text-xs text-gray-500 uppercase">Ready to withdraw</div><div className="mt-2 text-2xl font-bold font-display text-emerald-600">{formatINR(balance)}</div></div>
        <div className="card-base p-5"><div className="text-xs text-gray-500 uppercase">Total withdrawn</div><div className="mt-2 text-2xl font-bold font-display">{formatINR(w.total_withdrawn_paise)}</div></div>
      </div>

      {user.kyc_status !== "verified" && (
        <div className="rounded-xl bg-amber-50 border border-amber-200 p-4 flex items-start gap-3">
          <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5" />
          <div>
            <div className="font-medium">Verify your identity once before withdrawing</div>
            <p className="text-sm text-gray-600 mt-1">Takes 2 minutes.</p>
            <Link to="/onboarding/kyc" className="inline-flex mt-2 bg-amber-600 hover:bg-amber-700 text-white px-3 py-1.5 rounded-lg text-sm font-medium">Verify Now →</Link>
          </div>
        </div>
      )}

      {user.kyc_status === "verified" && (
        <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-5" data-testid="withdraw-card">
          <h3 className="text-lg font-semibold font-display">Ready to withdraw!</h3>
          <p className="text-sm text-gray-600 mt-1">{formatINR(balance)} is ready. Enter your UPI ID to get paid instantly.</p>
          <input data-testid="upi-input" value={upi} onChange={(e) => setUpi(e.target.value)} placeholder="yourname@upi or phone@upi" className="mt-3 w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm" />
          <p className="text-xs text-gray-500 mt-1">Works with PhonePe, GPay, Paytm, any UPI app</p>
          <button data-testid="withdraw-btn" onClick={withdraw} disabled={busy || !upi} className="mt-4 bg-emerald-500 hover:bg-emerald-600 btn-press text-white px-5 py-2.5 rounded-lg text-sm font-medium disabled:opacity-50 inline-flex items-center gap-2"><Wallet className="h-4 w-4" /> {busy ? "Sending…" : `Withdraw ${formatINR(balance)}`}</button>
        </div>
      )}

      <div className="card-base">
        <div className="px-5 py-4 border-b border-gray-100 font-semibold">Earnings history</div>
        {w.transactions.length === 0 ? (
          <div className="p-10 text-center text-sm text-gray-500">No earnings yet.<br/>Complete your first deal to start earning.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-[11px] text-gray-500 uppercase bg-gray-50">
              <tr><th className="text-left px-5 py-3">Date</th><th className="text-left px-5 py-3">Property / Detail</th><th className="text-left px-5 py-3">Type</th><th className="text-right px-5 py-3">Amount</th><th className="text-left px-5 py-3">Status</th></tr>
            </thead>
            <tbody>
              {w.transactions.map(t => (
                <tr key={t.id} className="border-t border-gray-100">
                  <td className="px-5 py-3 text-gray-600">{formatDate(t.created_at)}</td>
                  <td className="px-5 py-3">{t.property_address || t.description}</td>
                  <td className="px-5 py-3 text-gray-600">{walletTxTypeLabel(t.type)}</td>
                  <td className={`px-5 py-3 text-right font-medium ${t.type === "withdrawal_debit" ? "text-gray-600" : "text-emerald-600"}`}>{t.type === "withdrawal_debit" ? "-" : "+"}{formatINR(t.amount_paise)}</td>
                  <td className="px-5 py-3"><span className={`pill ${t.status === "completed" ? "bg-emerald-50 text-emerald-700" : t.status === "failed" ? "bg-red-50 text-red-700" : "bg-amber-50 text-amber-700"}`}>{t.status}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <MilestoneProgressWidget />

      <ReferralTrackingPanel />

      <MilestoneHistoryPanel />
    </div>
  );
}